import { api } from './client';

export interface DelhiveryConfig {
  id: number;
  env: string;
  clientName: string | null;
  pickupLocationName: string | null;
  pickupAddress: string | null;
  pickupPin: string | null;
  pickupPhone: string | null;
  sellerGstin: string | null;
  active: boolean;
  dryRun: boolean;
  /** NDR re-attempt / update details are sent to Delhivery only when on. */
  ndrLive?: boolean;
  warehouseRegistered: boolean;
  lastPolledAt: string | null;
  tokenSet: boolean;
}

export interface Shipment {
  id: number;
  outwardGrnId: number;
  awbNo: string | null;
  status: string;
  statusDetail: string | null;
  statusLocation: string | null;
  statusAt: string | null;
  paymentMode: string;
  /** Cash Delhivery collects on delivery (COD shipments). */
  codAmount?: string | number;
  boxCount: number;
  pickupId: string | null;
  pickupDate: string | null;
  error: string | null;
  /** Last tracked scans as JSON, newest first (may be truncated). */
  trackingJson?: string | null;
}

export interface ShippingRow {
  outwardGrnId: number;
  grnNo: string;
  grnDate: string;
  doNo: string;
  enqNo: string | null;
  orderName: string | null;
  /** Shopify order total incl GST. */
  orderValue?: number | null;
  /** How the order is paid — compare with shipment.paymentMode, as booked with Delhivery. */
  orderPayment?: { method: 'COD' | 'Prepaid'; codAmount: number | null } | null;
  consignee: string | null;
  city: string | null;
  pin: string | null;
  phone: string | null;
  boxCount: number;
  weightKg: number;
  shipment: Shipment | null;
  /** Sales Return that received this RTO parcel back, if any. */
  returnNo?: string | null;
}

export async function getDelhiveryConfig(): Promise<DelhiveryConfig> {
  return (await api.get<DelhiveryConfig>('/shipping/config')).data;
}
export async function updateDelhiveryConfig(dto: Partial<Omit<DelhiveryConfig, 'id' | 'tokenSet' | 'warehouseRegistered' | 'lastPolledAt'>>): Promise<DelhiveryConfig> {
  return (await api.patch<DelhiveryConfig>('/shipping/config', dto)).data;
}
export async function registerDelhiveryWarehouse(): Promise<{ ok: boolean }> {
  return (await api.post('/shipping/config/register-warehouse')).data;
}
export async function checkServiceability(pin: string): Promise<{ serviceable: boolean; prepaid: boolean; cod: boolean }> {
  return (await api.get('/shipping/serviceability', { params: { pin } })).data;
}
/** from / to: dispatch-date window (YYYY-MM-DD). */
export async function listShippingRows(params: { from?: string; to?: string } = {}): Promise<ShippingRow[]> {
  return (await api.get<ShippingRow[]>('/shipping/shipments', { params })).data;
}
export async function bookShipment(outwardGrnId: number): Promise<Shipment> {
  return (await api.post('/shipping/shipments/book', { outwardGrnId })).data;
}
export async function requestPickup(id: number, dto: { pickupDate?: string; pickupTime?: string } = {}): Promise<Shipment> {
  return (await api.post(`/shipping/shipments/${id}/pickup`, dto)).data;
}
export interface TrackScan {
  status: string;
  location: string;
  at: string | null;
  note: string;
  /** Delhivery ScanType: UD forward, RT return leg, DL delivered. */
  type?: string;
}
export async function trackShipment(id: number): Promise<Shipment & { scans: TrackScan[] }> {
  return (await api.post(`/shipping/shipments/${id}/track`)).data;
}
export type NdrActionKind = 'reattempt' | 'update' | 'rto' | 'note';
export interface NdrActionLog {
  action: NdrActionKind;
  at: string;
  by: string | null;
  note: string | null;
  requestId: string | null;
  dryRun: boolean;
  details: Record<string, string> | null;
  /** Delhivery's result for a live action: applied, refused (with reason), or still processing. */
  outcome: 'accepted' | 'failed' | 'pending' | null;
  outcomeMessage: string | null;
}
export interface NdrRow {
  shipmentId: number;
  awbNo: string;
  outwardGrnId: number;
  grnNo: string | null;
  orderName: string | null;
  /** Shopify order total incl GST. */
  orderValue?: number | null;
  consignee: string | null;
  phone: string | null;
  address: string | null;
  city: string | null;
  pin: string | null;
  value: number;
  paymentMode: string;
  codAmount: number;
  statusDetail: string | null;
  statusLocation: string | null;
  statusAt: string | null;
  reason: string | null;
  reasonCode: string | null;
  attempts: number;
  firstNdrAt: string | null;
  lastAttemptAt: string | null;
  actions: NdrActionLog[];
}
/** Failed-delivery queue. `live` is false while NDR actions run as a dry run. */
export async function getNdrQueue(): Promise<{ live: boolean; rows: NdrRow[] }> {
  return (await api.get('/shipping/ndr')).data;
}
export async function ndrAction(
  shipmentId: number,
  dto: { action: NdrActionKind; name?: string; phone?: string; address?: string; note?: string },
): Promise<{ ok: boolean; action: NdrActionKind; requestId: string | null; dryRun: boolean; outcome: 'accepted' | 'pending' | null; message: string }> {
  return (await api.post(`/shipping/shipments/${shipmentId}/ndr`, dto)).data;
}
/** COD order booked as Prepaid: switch the shipment to COD at Delhivery. */
export async function setShipmentCod(id: number): Promise<Shipment> {
  return (await api.post(`/shipping/shipments/${id}/payment-cod`)).data;
}
export async function cancelShipment(id: number): Promise<Shipment> {
  return (await api.post(`/shipping/shipments/${id}/cancel`)).data;
}
/** Fetch the label PDF (authed) and open it in a new tab. */
export async function openShipmentLabel(id: number): Promise<void> {
  const res = await api.get(`/shipping/shipments/${id}/label.pdf`, { responseType: 'blob' });
  const url = URL.createObjectURL(res.data as Blob);
  window.open(url, '_blank');
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}
